import * as Yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum K1893FreightLineItemKeys {
  name = 'name',
  dimensions = 'dimensions',
  stackable = 'stackable',
  weightPer = 'weightPer',
  qty = 'qty',
  weightTotal = 'weightTotal',
}

const K1893FreightLineItemSchema = Yup.object().shape({
  [K1893FreightLineItemKeys.name]: Yup.string().nullable().optional(),
  [K1893FreightLineItemKeys.dimensions]: Yup.string().nullable().optional(),
  [K1893FreightLineItemKeys.stackable]: Yup.string().nullable().optional(),
  [K1893FreightLineItemKeys.qty]: Yup.string().nullable().optional(),
  [K1893FreightLineItemKeys.weightPer]: Yup.string().nullable().optional(),
  [K1893FreightLineItemKeys.weightTotal]: Yup.string().nullable().optional(),
})

export const K1893FreightQuoteFileDataSchema = Yup.object().shape({
  quoteType: Yup.string().nullable().optional(),
  internalQuoteRefNumber: Yup.string().nullable().optional(),
  referencePONumber: Yup.string().nullable().optional(),
  pickupDate: Yup.string().nullable().optional(),
  requestedDeliveryDate: Yup.string().nullable().optional(),
  dateIssued: Yup.string().nullable().optional(),
  paymentTerms: Yup.string().nullable().optional(),
  freightTerms: Yup.string().nullable().optional(),
  issuer: Yup.object()
    .shape({
      companyName: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      phoneNumber: Yup.string().nullable().optional(),
      emailAddress: Yup.string().nullable().optional(),
    })
    .required(),
  pickupLocation: Yup.object()
    .shape({
      companyName: Yup.string().nullable().optional(),
      address: Yup.string().nullable().optional(),
      contactInfo: Yup.string().nullable().optional(),
      email: Yup.string().nullable().optional(),
      phone: Yup.string().nullable().optional(),
      apptNeeded: Yup.string().nullable().optional(),
      pickupHours: Yup.string().nullable().optional(),
      loadingDock: Yup.string().nullable().optional(),
      forklift: Yup.string().nullable().optional(),
      trailerSize: Yup.string().nullable().optional(),
    })
    .required(),
  destinationLocation: Yup.object()
    .shape({
      companyName: Yup.string().nullable().optional(),
      address: Yup.string().nullable().optional(),
      contactInfo: Yup.string().nullable().optional(),
      email: Yup.string().nullable().optional(),
      phone: Yup.string().nullable().optional(),
      apptNeeded: Yup.string().nullable().optional(),
      dropoffHours: Yup.string().nullable().optional(),
      loadingDock: Yup.string().nullable().optional(),
      forklift: Yup.string().nullable().optional(),
      trailerSize: Yup.string().nullable().optional(),
    })
    .required(),
  pallets: Yup.array().of(K1893FreightLineItemSchema).required(),
  instructionsHeader: Yup.string().nullable().optional(),
  comments: Yup.string().nullable().optional(),
})

export type K1893FreightQuoteFileData = Yup.InferType<typeof K1893FreightQuoteFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<K1893FreightQuoteFileData>

export const k1893FreightQuoteFileMappings: RequiredFileData = {
  issuer: {
    companyName: '467f1866-321d-489d-bbf5-1080aff65167',
    addressLine1: 'f1c1a410-7af5-4f22-acb2-0fd4c666ae06',
    phoneNumber: 'e4c720bd-d57c-450d-9c6a-c5a5958b3c24',
    emailAddress: '09937984-9c7f-4b3a-8dc1-11d6189bb582',
  },
  quoteType: 'd428128f-f89b-4920-8735-9269190dc8dc',
  internalQuoteRefNumber: '8289f791-01f6-4b3e-a478-5d86afee7f97',
  referencePONumber: '1f2e2b6e-5959-4bd7-94f4-d29ac65d11b9',
  pickupDate: '2a6cb9a2-948a-4826-9422-5a3647dd4c29',
  requestedDeliveryDate: '10f8ace2-f0a5-47fd-a24a-1a51a3c45465',
  dateIssued: 'c5a6b018-6c47-401a-a990-4e5b25d2a859',
  paymentTerms: 'c7da108b-4bbe-422e-8c96-1dbca26ba0a2',
  freightTerms: '81ab1770-dae5-494e-ac34-3cba0340d66d',
  pickupLocation: {
    companyName: 'dd5ad4a2-9c06-4406-a8dc-c10c0477f56f',
    address: 'f4a3d4c0-916c-4111-9ee0-9aec50e340e7',
    contactInfo: '8e307e32-a71d-4aeb-921d-073fa204c974',
    email: '0670d384-543a-484e-871e-8bd8cf23c6cb',
    phone: '7adf98ff-92bb-4c5b-85d3-ab26aad35a68',
    apptNeeded: '0625cda8-ff76-4da6-8fa8-654b04fbfced',
    pickupHours: '4da1e28f-2706-4f63-a174-82854898e826',
    loadingDock: 'df7c6bff-0109-440a-b714-c93b5ac71c67',
    forklift: 'e1627ac6-3228-4b4e-9e92-7fe0cf7d6f33',
    trailerSize: 'c247897e-67f9-4ebb-9b02-662b43091739',
  },
  destinationLocation: {
    companyName: '7c658377-3bd9-4552-83e5-28fca3b40f54',
    address: 'f51c6404-8aa2-462e-8470-86c3ba640973',
    contactInfo: 'a73286d3-708a-4ff6-aded-17493724239f',
    email: '91376e7b-5bca-42d1-a7c5-b630f31d7d9a',
    phone: 'ae394f29-79ab-47c1-91d5-93844be6c141',
    apptNeeded: '8f9f1987-3295-47d3-a52e-8284d2c43706',
    dropoffHours: 'ab0e3739-5609-436c-8ff0-1921997f9b8a',
    loadingDock: 'ab49268d-5cb2-49af-a978-6b0c958c7d33',
    forklift: 'bbefbb4a-ce62-44cc-a3bd-cc7edcf849f4',
    trailerSize: '587b3be7-978b-4e36-a4d3-d2ea56db501c',
  },
  pallets: [
    {
      name: '72a41996-c0bf-4597-af26-dde9c587a5a4',
      dimensions: '4898c6ad-4aed-497a-af9e-30b6701c61f3',
      stackable: '49b02f31-d06c-41d7-9f90-3fb0ae29beb1',
      qty: 'b2b5a968-78a3-44ea-ad84-47b6a7350b5b',
      weightPer: 'd7461349-1799-41c5-a8d8-ef5606b8faf1',
      weightTotal: '13b45c75-5669-44c9-aa46-0cc8b5c97ff7',
    },
  ],
  instructionsHeader: 'fcd516e6-6d73-46ce-9354-43360c601be4',
  comments: 'b7a0a980-802b-4f64-8407-a81e272c5a3a',
}
